import { FeatureNames, HemsProperties } from '@vi-iot/client';
import { FilterType } from '../../+installations/installation/energy-historical-data-modal/energy-filters-header/energy-filters.data';
import {
  getTransformedEnergyHoursDetails,
  getValuePrecision,
  transformValueByUnit,
} from '../../operational-data-common/helpers/energy';

import { Unit } from '../../shared/common/units';
import { HemsHistoricalData } from './energy-historical-data.model';

const forbiddenFeaturesConfig = [
  {
    filterType: FilterType.Day,
    forbiddenFeatures: [FeatureNames.emsCarbonSavings, FeatureNames.emsCarbonEmissions],
  },
  {
    filterType: FilterType.Month,
    forbiddenFeatures: [FeatureNames.energyBatteryStateOfCharge],
  },
  {
    filterType: FilterType.Year,
    forbiddenFeatures: [FeatureNames.energyBatteryStateOfCharge],
  },
  {
    filterType: FilterType.Total,
    forbiddenFeatures: [FeatureNames.energyBatteryStateOfCharge],
  },
];

export const powerProperties: HemsProperties[] = [
  FeatureNames.kpiAutarky,
  FeatureNames.kpiSelfConsumption,
  FeatureNames.energyProductionTotal,
  FeatureNames.energyConsumptionTotal,
  FeatureNames.energyHeatPumpConsumption,
  FeatureNames.energyHouseholdConsumption,
  FeatureNames.emsCarbonSavings,
  FeatureNames.emsCarbonEmissions,
  FeatureNames.energyGridFeedIn,
  FeatureNames.energyGridConsumption,
  FeatureNames.energyBatteryCharge,
  FeatureNames.energyBatteryDischarge,
  FeatureNames.energyBatteryStateOfCharge,
  FeatureNames.emsEnergyConsumptionVcs,
  FeatureNames.emsEnergyConsumptionPvImmersionHeater,
  FeatureNames.emsEnergyProductionToConsumption,
];

export const thermalProperties: HemsProperties[] = [
  FeatureNames.heatPumpConsumption,
  FeatureNames.compressorConsumption,
  FeatureNames.electricHeaterConsumption,
  FeatureNames.heatingConsumption,
  FeatureNames.hotWaterConsumption,
  FeatureNames.coolingConsumption,
  FeatureNames.thermalOutputTotal,
  FeatureNames.heatingOutput,
  FeatureNames.hotWaterOutput,
  FeatureNames.coolingOutput,
];

export enum HemsFlowType {
  Power = 'Power',
  Thermal = 'Thermal',
}

export function filterFeaturesByFilterType(
  features: HemsProperties[],
  filterType: FilterType
): HemsProperties[] {
  const forbiddenFeatures = forbiddenFeaturesConfig.find(
    (filter) => filterType === filter.filterType
  ).forbiddenFeatures;

  return features.filter((feature) => !forbiddenFeatures.includes(feature));
}

export function isFeatureOnForbiddenListForGivenFilter(
  feature: HemsProperties,
  filterType: FilterType
): boolean {
  const forbiddenFeatures = forbiddenFeaturesConfig.find(
    (filter) => filterType === filter.filterType
  ).forbiddenFeatures;

  return forbiddenFeatures.includes(feature);
}

/*
 * Adjust values and units of hems chart features to match Energy Hours units display requirements.
 */
export function getHEMSDataWithAdjustedTimeseries(
  hemsData: HemsHistoricalData,
  featuresToAdjust: HemsProperties[]
): HemsHistoricalData {
  const featuresToAdjustHelper: {
    feature: string;
    featureInitialSummaryValue: number;
    featureAdjustedSummaryValue: number;
    initialUnit: Unit;
    adjustedUnit: Unit;
  }[] = [];

  // 1. Construct Helper Object
  const unitWeights = {
    [Unit.WattHour]: 1,
    [Unit.KilowattHour]: 2,
    [Unit.MegawattHour]: 3,
  };
  let highestUnitFound: Unit = Unit.WattHour;

  // 1.1 find highest value within summary and based on that set type of all features
  featuresToAdjust.forEach((entry) => {
    const featureUnit = hemsData.timeseries_properties[entry] as Unit;
    const isEnergyHourUnit = featureUnit?.includes(Unit.WattHour);

    if (isEnergyHourUnit) {
      let highestTimeSeriesValue = 0;
      hemsData.data.timeseries.forEach((series) => {
        const seriesFeatureValue = series[entry] as number;
        if (seriesFeatureValue > highestTimeSeriesValue) {
          highestTimeSeriesValue = seriesFeatureValue as number;
        }
      });
      const { unit } = getTransformedEnergyHoursDetails(highestTimeSeriesValue, featureUnit);

      const unitWeight = unitWeights[unit];
      const highestUnitFoundWeight = unitWeights[highestUnitFound];

      if (unitWeight > highestUnitFoundWeight) {
        highestUnitFound = unit;
      }
    }
  });

  // 2.2 construct helper object for featuresToAdjust
  featuresToAdjust.forEach((entry) => {
    const properyUnit = hemsData.timeseries_properties[entry] as Unit;
    const isEnergyHourUnit = properyUnit?.includes(Unit.WattHour);

    if (isEnergyHourUnit) {
      const adjustedSummaryValue = transformValueByUnit(
        hemsData.data.summary[entry],
        properyUnit,
        highestUnitFound
      );

      featuresToAdjustHelper.push({
        feature: entry,
        featureInitialSummaryValue: hemsData.data.summary[entry],
        featureAdjustedSummaryValue: adjustedSummaryValue,
        initialUnit: properyUnit,
        adjustedUnit: highestUnitFound,
      });
    }
  });

  // Adjust values and units
  featuresToAdjustHelper.forEach((entry) => {
    //summary
    hemsData.timeseries_properties[entry.feature] = entry.adjustedUnit;
    hemsData.data.summary[entry.feature] = !!entry.featureAdjustedSummaryValue
      ? +entry.featureAdjustedSummaryValue.toFixed(
          getValuePrecision(entry.featureAdjustedSummaryValue, entry.adjustedUnit)
        )
      : null;

    //timeseries
    hemsData.data.timeseries.forEach((serie) => {
      const seriesPropertyValue = serie[entry.feature] as number;
      const adjustedSeriesPropertyValue = transformValueByUnit(
        seriesPropertyValue,
        entry.initialUnit,
        highestUnitFound
      );

      serie[entry.feature] = !!adjustedSeriesPropertyValue
        ? +adjustedSeriesPropertyValue.toFixed(
            getValuePrecision(adjustedSeriesPropertyValue, highestUnitFound)
          )
        : null;
    });
  });

  return hemsData;
}
